// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  getIdToken,
} from "firebase/auth";
import { doc, setDoc, getDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db, googleProvider } from "../services/firebase";



const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubFirestore = () => {};

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadOrCreateUserDoc(firebaseUser);

        // ── Real-time listener so navbar stays in sync ──
        unsubFirestore();
        unsubFirestore = onSnapshot(doc(db, "users", firebaseUser.uid), (snap) => {
          if (snap.exists()) setUserData(snap.data());
        });
      } else {
        setUser(null);
        setUserData(null);
        unsubFirestore();
      }
      setLoading(false);
    });

    return () => {
      unsubAuth();
      unsubFirestore();
    };
  }, []);

  const loadOrCreateUserDoc = async (firebaseUser) => {
    const ref = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const newDoc = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        plan: "free",
        videosUsedToday: 0,
        videosUsedMonth: 0,
        lastResetDate: new Date().toDateString(),
        createdAt: serverTimestamp(),
        lastUsedWeek:"",
        lastUsedMonth:""
      };
      await setDoc(ref, newDoc);
      setUserData(newDoc);
    } else {

      const data    = snap.data();
      const updates = {};
      if (data.videosUsedMonth === undefined) updates.videosUsedMonth = 0;
      if (data.videosUsedToday === undefined) updates.videosUsedToday = 0;
      if (data.lastUsedMonth   === undefined) updates.lastUsedMonth   = "";
      if (data.lastUsedWeek    === undefined) updates.lastUsedWeek    = "";

      if (Object.keys(updates).length > 0) {
        await setDoc(ref, updates, { merge: true });
      }
      const freshSnap = await getDoc(ref);
      setUserData(freshSnap.data());  
      
    }
  };

  const loginWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const refreshUserData = async () => {
    if (!user) return;
    const snap = await getDoc(doc(db, "users", user.uid));
    if (snap.exists()) setUserData(snap.data());
  };

  const getToken = async () => {
    if (!auth.currentUser) throw new Error("No logged-in user");
    return await getIdToken(auth.currentUser, false);
  };

  return (
    <AuthContext.Provider value={{ user, userData, loading, loginWithGoogle, logout, refreshUserData,getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);